import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Button,
	FormControl,
	FormLabel,
	Select,
	Input,
	Textarea,
	VStack,
	useToast,
	Spinner,
	Center,
	Checkbox,
	Heading,
	Box,
	HStack,
	Grid,
	GridItem,
	Card,
	CardBody,
	CardHeader,
	Text,
	Stepper,
	Step,
	StepIndicator,
	StepStatus,
	StepIcon,
	StepNumber,
	StepTitle,
	StepSeparator,
	useSteps,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { useState, useEffect, useCallback } from 'react';
import {
	generarDocumento,
	actualizarDocumento,
} from '../../services/documentos';
import { errorNotification, okNotification } from '../../utils/Notificaciones';
import {
	DOCUMENTOS_ENCARGOS,
	DOCUMENTOS_PEDIDOS,
} from './constants/documentTypes';
import { CAMPOS_POR_TIPO } from './constants/documentFields';
import {
	cargarDatosEncargo,
	cargarDatosPedido,
	requiereAutofill,
} from './utils/documentDataLoader';

const GenerarDocumentoModal = ({
	isOpen,
	onClose,
	pedidoUuid,
	encargoUuid,
	onSuccess,
	documentoParaEditar = null, // Nuevo prop para editar documento
}) => {
	const [tipoSeleccionado, setTipoSeleccionado] = useState('');
	const [formData, setFormData] = useState({});
	const [isGenerating, setIsGenerating] = useState(false);
	const [isLoadingData, setIsLoadingData] = useState(false);
	const [seccionesOpcionalesVisibles, setSeccionesOpcionalesVisibles] =
		useState({});
	const toast = useToast();

	const esEdicion = !!documentoParaEditar;

	// Determinar qué documentos mostrar según el contexto
	const documentosDisponibles = encargoUuid
		? DOCUMENTOS_ENCARGOS
		: DOCUMENTOS_PEDIDOS;

	// Stepper control
	const steps = [{ title: 'Tipo de Documento' }, { title: 'Datos' }];
	const { activeStep, setActiveStep } = useSteps({
		index: esEdicion ? 1 : 0, // Si estamos editando, empezar en paso 2
		count: steps.length,
	});

	const loadAndPrefillData = useCallback(async () => {
		try {
			setIsLoadingData(true);
			let data = {};

			if (encargoUuid) {
				data = await cargarDatosEncargo(encargoUuid, tipoSeleccionado);
			} else if (pedidoUuid) {
				data = await cargarDatosPedido(pedidoUuid, tipoSeleccionado);
			}

			setFormData(data);
		} catch (error) {
			console.error('Error cargando datos:', error);
			errorNotification('Error al cargar los datos', toast);
		} finally {
			setIsLoadingData(false);
		}
	}, [tipoSeleccionado, pedidoUuid, encargoUuid, toast]);

	// Efecto para cargar y precargar datos cuando se abre el modal
	useEffect(() => {
		if (isOpen) {
			// Si estamos editando, cargar datos del documento
			if (esEdicion && documentoParaEditar) {
				setTipoSeleccionado(documentoParaEditar.tipo);
				setFormData(documentoParaEditar.form_data || {});
				setActiveStep(1); // Ir directamente al paso 2
			}
			// Si no estamos editando y hay autofill, cargar datos
			else if (
				requiereAutofill(tipoSeleccionado, pedidoUuid, encargoUuid)
			) {
				loadAndPrefillData();
			}
		} else {
			// Resetear el stepper cuando se cierra el modal
			setActiveStep(0);
		}
	}, [
		isOpen,
		tipoSeleccionado,
		pedidoUuid,
		encargoUuid,
		loadAndPrefillData,
		esEdicion,
		documentoParaEditar,
		setActiveStep,
	]);

	const handleTipoChange = e => {
		const tipo = e.target.value;
		setTipoSeleccionado(tipo);
		setFormData({});
		setSeccionesOpcionalesVisibles({});
		// Avanzar automáticamente al siguiente paso
		if (tipo && !esEdicion) {
			setActiveStep(1);
		}
	};

	const handleNextStep = () => {
		if (activeStep === 0 && !tipoSeleccionado) {
			errorNotification('Debes seleccionar un tipo de documento', toast);
			return;
		}
		setActiveStep(activeStep + 1);
	};

	const handlePrevStep = () => {
		setActiveStep(activeStep - 1);
	};

	const handleFieldChange = (fieldName, value) => {
		setFormData(prev => ({
			...prev,
			[fieldName]: value,
		}));
	};

	const handleCheckboxChange = (fieldName, isChecked) => {
		setFormData(prev => ({
			...prev,
			[fieldName]: isChecked ? 'X' : '',
		}));
	};

	const toggleSeccionOpcional = seccionId => {
		setSeccionesOpcionalesVisibles(prev => ({
			...prev,
			[seccionId]: !prev[seccionId],
		}));
	};

	const handleGenerar = async () => {
		if (!tipoSeleccionado) {
			errorNotification('Debes seleccionar un tipo de documento', toast);
			return;
		}

		try {
			setIsGenerating(true);

			if (esEdicion) {
				// Actualizar documento existente
				await actualizarDocumento(documentoParaEditar.uuid, formData);
				okNotification('Documento actualizado correctamente', toast);
			} else {
				// Generar nuevo documento
				await generarDocumento({
					tipo: tipoSeleccionado,
					data: formData,
					pedido_uuid: pedidoUuid,
					encargo_uuid: encargoUuid,
				});
				okNotification('Documento generado correctamente', toast);
			}

			setTipoSeleccionado('');
			setFormData({});
			onClose();
			onSuccess && onSuccess();
		} catch (error) {
			console.error('Error con documento:', error);
			errorNotification(
				error?.data?.message ||
					`Error al ${esEdicion ? 'actualizar' : 'generar'} el documento`,
				toast,
			);
		} finally {
			setIsGenerating(false);
		}
	};

	const campos = tipoSeleccionado
		? CAMPOS_POR_TIPO[tipoSeleccionado] || []
		: [];

	// Agrupar campos por secciones
	const agruparCamposPorSeccion = () => {
		const grupos = [];
		let grupoActual = {
			titulo: 'Información General',
			campos: [],
			esOpcional: false,
		};

		campos.forEach((campo, index) => {
			if (campo.type === 'section') {
				// Si hay campos en el grupo actual, guardarlo
				if (grupoActual.campos.length > 0) {
					grupos.push(grupoActual);
				}
				// Verificar si es una sección opcional
				const esOpcional = campo.label
					.toLowerCase()
					.includes('opcional');
				// Crear nuevo grupo
				grupoActual = {
					titulo: campo.label,
					campos: [],
					esOpcional,
					id: campo.label.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
				};
			} else {
				grupoActual.campos.push(campo);
			}
		});

		// Añadir el último grupo si tiene campos
		if (grupoActual.campos.length > 0) {
			grupos.push(grupoActual);
		}

		return grupos;
	};

	const gruposCampos = agruparCamposPorSeccion();

	// Función para renderizar un campo
	const renderCampo = campo => {
		// Renderizar checkbox
		if (campo.type === 'checkbox') {
			return (
				<FormControl key={campo.name}>
					<Checkbox
						isChecked={formData[campo.name] === 'X'}
						onChange={e =>
							handleCheckboxChange(campo.name, e.target.checked)
						}
					>
						{campo.label}
					</Checkbox>
				</FormControl>
			);
		}

		// Renderizar textarea
		if (campo.type === 'textarea') {
			return (
				<FormControl key={campo.name}>
					<FormLabel fontSize='sm' fontWeight='medium'>
						{campo.label}
					</FormLabel>
					<Textarea
						value={formData[campo.name] || ''}
						onChange={e =>
							handleFieldChange(campo.name, e.target.value)
						}
						rows={3}
						size='sm'
						minH='90px'
					/>
				</FormControl>
			);
		}

		// Renderizar input por defecto
		return (
			<FormControl key={campo.name}>
				<FormLabel fontSize='sm' fontWeight='medium'>
					{campo.label}
				</FormLabel>
				<Input
					type={campo.type}
					value={formData[campo.name] || ''}
					onChange={e =>
						handleFieldChange(campo.name, e.target.value)
					}
					size='sm'
				/>
			</FormControl>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size='6xl'
			scrollBehavior='inside'
		>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>
					{esEdicion ? 'Editar Documento' : 'Generar Documento'}
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{/* Stepper */}
					<Stepper
						index={activeStep}
						orientation={{ base: 'vertical', md: 'horizontal' }}
						mb={6}
						colorScheme='blue'
					>
						{steps.map((step, index) => (
							<Step key={index}>
								<StepIndicator>
									<StepStatus
										complete={<StepIcon />}
										incomplete={<StepNumber />}
										active={<StepNumber />}
									/>
								</StepIndicator>

								<Box flexShrink='0'>
									<StepTitle>{step.title}</StepTitle>
								</Box>

								<StepSeparator
									style={{
										height: '2px',
										marginTop: '0',
									}}
								/>
							</Step>
						))}
					</Stepper>

					{isLoadingData ? (
						<Center py={8}>
							<Spinner size='lg' />
						</Center>
					) : (
						<>
							{/* Paso 1: Selector de tipo de documento */}
							{activeStep === 0 && (
								<Card>
									<CardBody>
										<FormControl isRequired>
											<FormLabel
												fontWeight='bold'
												fontSize='lg'
											>
												Tipo de Documento
											</FormLabel>
											<Select
												placeholder='Selecciona un tipo'
												value={tipoSeleccionado}
												onChange={handleTipoChange}
												size='lg'
											>
												{documentosDisponibles.map(
													tipo => (
														<option
															key={tipo.value}
															value={tipo.value}
														>
															{tipo.label}
														</option>
													),
												)}
											</Select>
										</FormControl>
									</CardBody>
								</Card>
							)}

							{/* Paso 2: Formulario con campos dinámicos */}
							{activeStep === 1 && tipoSeleccionado && (
								<VStack align='stretch' spacing={4}>
									{/* Indicador del tipo de documento seleccionado */}
									<Box
										bg='blue.50'
										border='1px'
										borderColor='blue.200'
										borderRadius='md'
										p={3}
									>
										<HStack>
											<Text
												fontSize='sm'
												fontWeight='medium'
												color='gray.600'
											>
												Documento:
											</Text>
											<Text
												fontSize='sm'
												fontWeight='bold'
												color='blue.700'
											>
												{documentosDisponibles.find(
													d =>
														d.value ===
														tipoSeleccionado,
												)?.label || tipoSeleccionado}
											</Text>
										</HStack>
									</Box>
									{gruposCampos.map((grupo, grupoIndex) => {
										// Si es una sección opcional
										if (grupo.esOpcional) {
											const estaVisible =
												seccionesOpcionalesVisibles[
													grupo.id
												];

											return (
												<Box key={grupoIndex}>
													{/* Checkbox para mostrar/ocultar sección opcional */}
													<Card
														bg='gray.100'
														mb={estaVisible ? 2 : 0}
													>
														<CardBody py={3}>
															<Checkbox
																isChecked={
																	estaVisible
																}
																onChange={() =>
																	toggleSeccionOpcional(
																		grupo.id,
																	)
																}
																colorScheme='blue'
																size='lg'
															>
																<Text
																	fontSize='sm'
																	fontWeight='medium'
																>
																	{grupo.titulo.replace(
																		/\s*-?\s*(opcional|OPCIONAL|\(OPCIONAL\)|\(opcional\))/gi,
																		'',
																	)}
																</Text>
															</Checkbox>
														</CardBody>
													</Card>

													{/* Card con los campos (solo visible si el checkbox está activado) */}
													{estaVisible && (
														<Card>
															<CardHeader
																bg='blue.50'
																py={3}
															>
																<Heading size='sm'>
																	{
																		grupo.titulo
																	}
																</Heading>
															</CardHeader>
															<CardBody bg='gray.50'>
																<Grid
																	templateColumns={{
																		base: 'repeat(1, 1fr)',
																		md: 'repeat(2, 1fr)',
																	}}
																	gap={4}
																>
																	{grupo.campos.map(
																		(
																			campo,
																			campoIndex,
																		) => {
																			// Detectar si es un campo de fecha (día, mes, año)
																			const esDia =
																				campo.name
																					.toLowerCase()
																					.includes(
																						'dia',
																					) ||
																				campo.name
																					.toLowerCase()
																					.includes(
																						'día',
																					);
																			const esMes =
																				campo.name
																					.toLowerCase()
																					.includes(
																						'mes',
																					);
																			const esAnio =
																				campo.name
																					.toLowerCase()
																					.includes(
																						'year',
																					) ||
																				campo.name
																					.toLowerCase()
																					.includes(
																						'año',
																					);

																			// Si es día, buscar mes y año consecutivos
																			if (
																				esDia &&
																				campoIndex <
																					grupo
																						.campos
																						.length -
																						2
																			) {
																				const siguienteCampo =
																					grupo
																						.campos[
																						campoIndex +
																							1
																					];
																				const tercerCampo =
																					grupo
																						.campos[
																						campoIndex +
																							2
																					];

																				const siguienteEsMes =
																					siguienteCampo.name
																						.toLowerCase()
																						.includes(
																							'mes',
																						);
																				const terceroEsAnio =
																					tercerCampo.name
																						.toLowerCase()
																						.includes(
																							'year',
																						) ||
																					tercerCampo.name
																						.toLowerCase()
																						.includes(
																							'año',
																						);

																				if (
																					siguienteEsMes &&
																					terceroEsAnio
																				) {
																					// Renderizar los tres campos juntos
																					return (
																						<GridItem
																							key={
																								campo.name
																							}
																							colSpan={{
																								base: 1,
																								md: 2,
																							}}
																						>
																							<FormControl>
																								<FormLabel
																									fontSize='sm'
																									fontWeight='medium'
																								>
																									Fecha
																								</FormLabel>
																								<HStack>
																									<Box flex='1'>
																										<FormLabel
																											fontSize='xs'
																											mb={
																												1
																											}
																										>
																											Día
																										</FormLabel>
																										<Input
																											type={
																												campo.type
																											}
																											value={
																												formData[
																													campo
																														.name
																												] ||
																												''
																											}
																											onChange={e =>
																												handleFieldChange(
																													campo.name,
																													e
																														.target
																														.value,
																												)
																											}
																											size='sm'
																										/>
																									</Box>
																									<Box flex='1'>
																										<FormLabel
																											fontSize='xs'
																											mb={
																												1
																											}
																										>
																											Mes
																										</FormLabel>
																										<Input
																											type={
																												siguienteCampo.type
																											}
																											value={
																												formData[
																													siguienteCampo
																														.name
																												] ||
																												''
																											}
																											onChange={e =>
																												handleFieldChange(
																													siguienteCampo.name,
																													e
																														.target
																														.value,
																												)
																											}
																											size='sm'
																										/>
																									</Box>
																									<Box flex='1'>
																										<FormLabel
																											fontSize='xs'
																											mb={
																												1
																											}
																										>
																											Año
																										</FormLabel>
																										<Input
																											type={
																												tercerCampo.type
																											}
																											value={
																												formData[
																													tercerCampo
																														.name
																												] ||
																												''
																											}
																											onChange={e =>
																												handleFieldChange(
																													tercerCampo.name,
																													e
																														.target
																														.value,
																												)
																											}
																											size='sm'
																										/>
																									</Box>
																								</HStack>
																							</FormControl>
																						</GridItem>
																					);
																				}
																			}

																			// Si es mes o año y el anterior era día, ya fue renderizado
																			if (
																				(esMes ||
																					esAnio) &&
																				campoIndex >
																					0
																			) {
																				const anteriorCampo =
																					grupo
																						.campos[
																						campoIndex -
																							1
																					];
																				const anteriorEsDia =
																					anteriorCampo.name
																						.toLowerCase()
																						.includes(
																							'dia',
																						) ||
																					anteriorCampo.name
																						.toLowerCase()
																						.includes(
																							'día',
																						);

																				if (
																					anteriorEsDia &&
																					esMes
																				) {
																					return null; // Ya renderizado
																				}
																				if (
																					esMes &&
																					campoIndex >
																						1 &&
																					esAnio
																				) {
																					const dosAnteriores =
																						grupo
																							.campos[
																							campoIndex -
																								2
																						];
																					if (
																						dosAnteriores.name
																							.toLowerCase()
																							.includes(
																								'dia',
																							) ||
																						dosAnteriores.name
																							.toLowerCase()
																							.includes(
																								'día',
																							)
																					) {
																						return null; // Ya renderizado
																					}
																				}
																				if (
																					esAnio &&
																					campoIndex >
																						1
																				) {
																					const dosAnteriores =
																						grupo
																							.campos[
																							campoIndex -
																								2
																						];
																					const anteriorEsMes =
																						anteriorCampo.name
																							.toLowerCase()
																							.includes(
																								'mes',
																							);
																					const dosAnterioresEsDia =
																						dosAnteriores.name
																							.toLowerCase()
																							.includes(
																								'dia',
																							) ||
																						dosAnteriores.name
																							.toLowerCase()
																							.includes(
																								'día',
																							);

																					if (
																						anteriorEsMes &&
																						dosAnterioresEsDia
																					) {
																						return null; // Ya renderizado
																					}
																				}
																			}

																			// Renderizar checkbox en toda la fila
																			if (
																				campo.type ===
																				'checkbox'
																			) {
																				return (
																					<GridItem
																						key={
																							campo.name
																						}
																						colSpan={{
																							base: 1,
																							md: 2,
																						}}
																					>
																						{renderCampo(
																							campo,
																						)}
																					</GridItem>
																				);
																			}

																			// Renderizar textarea en toda la fila
																			if (
																				campo.type ===
																				'textarea'
																			) {
																				return (
																					<GridItem
																						key={
																							campo.name
																						}
																						colSpan={{
																							base: 1,
																							md: 2,
																						}}
																					>
																						{renderCampo(
																							campo,
																						)}
																					</GridItem>
																				);
																			}

																			// Renderizar campo normal
																			return (
																				<GridItem
																					key={
																						campo.name
																					}
																				>
																					{renderCampo(
																						campo,
																					)}
																				</GridItem>
																			);
																		},
																	)}
																</Grid>
															</CardBody>
														</Card>
													)}
												</Box>
											);
										}

										// Si es una sección normal (no opcional)
										return (
											<Card key={grupoIndex}>
												<CardHeader bg='blue.50' py={3}>
													<Heading size='sm'>
														{grupo.titulo}
													</Heading>
												</CardHeader>
												<CardBody bg='gray.50'>
													<Grid
														templateColumns={{
															base: 'repeat(1, 1fr)',
															md: 'repeat(2, 1fr)',
														}}
														gap={4}
													>
														{grupo.campos.map(
															(
																campo,
																campoIndex,
															) => {
																// [Aquí va la misma lógica de renderizado que para las secciones opcionales]
																// Por simplicidad, usar la función renderCampo para campos normales

																// Detectar si es un campo de fecha
																const esDia =
																	campo.name
																		.toLowerCase()
																		.includes(
																			'dia',
																		) ||
																	campo.name
																		.toLowerCase()
																		.includes(
																			'día',
																		);
																const esMes =
																	campo.name
																		.toLowerCase()
																		.includes(
																			'mes',
																		);
																const esAnio =
																	campo.name
																		.toLowerCase()
																		.includes(
																			'year',
																		) ||
																	campo.name
																		.toLowerCase()
																		.includes(
																			'año',
																		);

																// Si es día, buscar mes y año consecutivos
																if (
																	esDia &&
																	campoIndex <
																		grupo
																			.campos
																			.length -
																			2
																) {
																	const siguienteCampo =
																		grupo
																			.campos[
																			campoIndex +
																				1
																		];
																	const tercerCampo =
																		grupo
																			.campos[
																			campoIndex +
																				2
																		];

																	const siguienteEsMes =
																		siguienteCampo.name
																			.toLowerCase()
																			.includes(
																				'mes',
																			);
																	const terceroEsAnio =
																		tercerCampo.name
																			.toLowerCase()
																			.includes(
																				'year',
																			) ||
																		tercerCampo.name
																			.toLowerCase()
																			.includes(
																				'año',
																			);

																	if (
																		siguienteEsMes &&
																		terceroEsAnio
																	) {
																		// Renderizar los tres campos juntos
																		return (
																			<GridItem
																				key={
																					campo.name
																				}
																				colSpan={{
																					base: 1,
																					md: 2,
																				}}
																			>
																				<FormControl>
																					<FormLabel
																						fontSize='sm'
																						fontWeight='medium'
																					>
																						Fecha
																					</FormLabel>
																					<HStack>
																						<Box flex='1'>
																							<FormLabel
																								fontSize='xs'
																								mb={
																									1
																								}
																							>
																								Día
																							</FormLabel>
																							<Input
																								type={
																									campo.type
																								}
																								value={
																									formData[
																										campo
																											.name
																									] ||
																									''
																								}
																								onChange={e =>
																									handleFieldChange(
																										campo.name,
																										e
																											.target
																											.value,
																									)
																								}
																								size='sm'
																							/>
																						</Box>
																						<Box flex='1'>
																							<FormLabel
																								fontSize='xs'
																								mb={
																									1
																								}
																							>
																								Mes
																							</FormLabel>
																							<Input
																								type={
																									siguienteCampo.type
																								}
																								value={
																									formData[
																										siguienteCampo
																											.name
																									] ||
																									''
																								}
																								onChange={e =>
																									handleFieldChange(
																										siguienteCampo.name,
																										e
																											.target
																											.value,
																									)
																								}
																								size='sm'
																							/>
																						</Box>
																						<Box flex='1'>
																							<FormLabel
																								fontSize='xs'
																								mb={
																									1
																								}
																							>
																								Año
																							</FormLabel>
																							<Input
																								type={
																									tercerCampo.type
																								}
																								value={
																									formData[
																										tercerCampo
																											.name
																									] ||
																									''
																								}
																								onChange={e =>
																									handleFieldChange(
																										tercerCampo.name,
																										e
																											.target
																											.value,
																									)
																								}
																								size='sm'
																							/>
																						</Box>
																					</HStack>
																				</FormControl>
																			</GridItem>
																		);
																	}
																}

																// Si es mes o año y el anterior era día, ya fue renderizado
																if (
																	(esMes ||
																		esAnio) &&
																	campoIndex >
																		0
																) {
																	const anteriorCampo =
																		grupo
																			.campos[
																			campoIndex -
																				1
																		];
																	const anteriorEsDia =
																		anteriorCampo.name
																			.toLowerCase()
																			.includes(
																				'dia',
																			) ||
																		anteriorCampo.name
																			.toLowerCase()
																			.includes(
																				'día',
																			);

																	if (
																		anteriorEsDia &&
																		esMes
																	) {
																		return null;
																	}
																	if (
																		esMes &&
																		campoIndex >
																			1 &&
																		esAnio
																	) {
																		const dosAnteriores =
																			grupo
																				.campos[
																				campoIndex -
																					2
																			];
																		if (
																			dosAnteriores.name
																				.toLowerCase()
																				.includes(
																					'dia',
																				) ||
																			dosAnteriores.name
																				.toLowerCase()
																				.includes(
																					'día',
																				)
																		) {
																			return null;
																		}
																	}
																	if (
																		esAnio &&
																		campoIndex >
																			1
																	) {
																		const dosAnteriores =
																			grupo
																				.campos[
																				campoIndex -
																					2
																			];
																		const anteriorEsMes =
																			anteriorCampo.name
																				.toLowerCase()
																				.includes(
																					'mes',
																				);
																		const dosAnterioresEsDia =
																			dosAnteriores.name
																				.toLowerCase()
																				.includes(
																					'dia',
																				) ||
																			dosAnteriores.name
																				.toLowerCase()
																				.includes(
																					'día',
																				);

																		if (
																			anteriorEsMes &&
																			dosAnterioresEsDia
																		) {
																			return null;
																		}
																	}
																}

																// Renderizar checkbox en toda la fila
																if (
																	campo.type ===
																	'checkbox'
																) {
																	return (
																		<GridItem
																			key={
																				campo.name
																			}
																			colSpan={{
																				base: 1,
																				md: 2,
																			}}
																		>
																			{renderCampo(
																				campo,
																			)}
																		</GridItem>
																	);
																}

																// Renderizar textarea en toda la fila
																if (
																	campo.type ===
																	'textarea'
																) {
																	return (
																		<GridItem
																			key={
																				campo.name
																			}
																			colSpan={{
																				base: 1,
																				md: 2,
																			}}
																		>
																			{renderCampo(
																				campo,
																			)}
																		</GridItem>
																	);
																}

																// Renderizar campo normal
																return (
																	<GridItem
																		key={
																			campo.name
																		}
																	>
																		{renderCampo(
																			campo,
																		)}
																	</GridItem>
																);
															},
														)}
													</Grid>
												</CardBody>
											</Card>
										);
									})}
								</VStack>
							)}
						</>
					)}
				</ModalBody>

				<ModalFooter>
					<HStack
						spacing={3}
						width='full'
						justifyContent='space-between'
					>
						<HStack>
							{/* Botón Atrás */}
							{activeStep > 0 && (
								<Button
									leftIcon={<ChevronLeftIcon />}
									variant='ghost'
									onClick={handlePrevStep}
								>
									Atrás
								</Button>
							)}
						</HStack>
						<HStack>
							<Button variant='ghost' onClick={onClose}>
								Cancelar
							</Button>
							{/* Botón Siguiente */}
							{activeStep === 0 && tipoSeleccionado && (
								<Button
									rightIcon={<ChevronRightIcon />}
									colorScheme='blue'
									onClick={handleNextStep}
								>
									Siguiente
								</Button>
							)}
							{/* Botón Generar/Actualizar */}
							{activeStep === 1 && (
								<Button
									colorScheme='blue'
									onClick={handleGenerar}
									isLoading={isGenerating}
									isDisabled={!tipoSeleccionado}
								>
									{esEdicion
										? 'Actualizar Borrador'
										: 'Generar Borrador'}
								</Button>
							)}
						</HStack>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default GenerarDocumentoModal;
